import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import axios from 'axios';
import baseURL from '../../../baseURL';
import GirviPageShell from '../../common/GirviPageShell';
import { PledgePhotosRow } from '../../common/PhotoThumb';
import { fingerprintService } from '../../../services/fingerprintService';
import { calendarDaysBetween } from '../../../utils/dharaInterest';

function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

function formatDate(dateString) {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return 'N/A';
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  return `${day}/${month}/${date.getFullYear()}`;
}

function paidReceiptRows(pledge) {
  const rows = (Array.isArray(pledge?.receipts) ? pledge.receipts : []).map((r) => {
    const interest = round2(
      r?.adjustments?.interestAdjustment ??
      r?.interestDetails?.calculatedInterest ??
      r?.illegalInterestAmount ??
      0
    );
    const principal = round2(
      r?.adjustments?.principalAdjustment ??
      r?.interestDetails?.illegalPrincipalAmount ??
      r?.illegalPrincipalAmount ??
      0
    );
    const total = round2(r?.totalAmount ?? interest + principal);
    return {
      key: r?._id || r?.receiptNo || `${r?.date}-${total}`,
      date: r?.date,
      no: r?.receiptNo || '',
      interest,
      principal,
      total,
    };
  }).filter((r) => r.total > 0 || r.interest > 0 || r.principal > 0);

  if (rows.length > 0) return rows;
  const interest = round2(pledge?.totalInterestPaid);
  const principal = round2(pledge?.totalPrincipalPaid);
  if (interest <= 0 && principal <= 0) return [];
  return [{
    key: 'totals',
    date: pledge?.lastPaymentDate,
    no: '',
    interest,
    principal,
    total: round2(interest + principal),
  }];
}

const CreateReceipt = ({ initialTicket = '', embedded = false }) => {
  const [searchParams] = useSearchParams();
  const ticketFromUrl = searchParams.get('ticket') || '';
  const [searchType, setSearchType] = useState('firstName');
  const [searchValue, setSearchValue] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [ticketNumber, setTicketNumber] = useState('');
  const [pledgeData, setPledgeData] = useState(null);
  const [receiptDate, setReceiptDate] = useState(() => new Date());
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(false);
  const [entryMode, setEntryMode] = useState('months');
  const [months, setMonths] = useState('');
  const [interestAmount, setInterestAmount] = useState('');
  const [principalAmount, setPrincipalAmount] = useState('');
  const searchTimeout = useRef(null);

  const fullPrincipal = Number(pledgeData?.loanDetails?.principalAmount) || 0;
  const pendingPrincipal = Math.max(0, round2(fullPrincipal - (Number(pledgeData?.totalPrincipalPaid) || 0)));
  const rate = Number(pledgeData?.loanDetails?.rateOfInterest) || 0;

  const monthInterest = round2((fullPrincipal * rate / 100 / 12) * (parseInt(months, 10) || 0));

  useEffect(() => {
    if (!pledgeData || entryMode !== 'months') return;
    const count = parseInt(months, 10) || 0;
    setInterestAmount(count > 0 ? String(monthInterest) : '');
  }, [pledgeData, entryMode, months, monthInterest]);

  const handleSearch = async (value, type = null) => {
    try {
      setError('');
      setSearchLoading(true);
      if (type) {
        setSearchType(type);
        setSearchValue(value);
      }
      if (!String(value || '').trim()) {
        setSearchResults([]);
        setSearchLoading(false);
        return;
      }
      const token = localStorage.getItem('token');
      const response = await axios.get(`${baseURL}/pledges/search`, {
        params: { searchType: type || searchType, searchValue: String(value).trim() },
        headers: { Authorization: `Bearer ${token}` },
      });
      const rows = response.data.data?.pledges || [];
      setSearchResults(rows.filter((p) => p.status !== 'RELEASED' && !p.loanDetails?.released));
    } catch (err) {
      setError(err.response?.data?.message || 'Error searching pledges');
    } finally {
      setSearchLoading(false);
    }
  };

  const fetchPledgeData = async (ticketNo) => {
    const ticket = String(ticketNo || '').trim();
    if (!ticket) return;
    try {
      setError('');
      const token = localStorage.getItem('token');
      const response = await axios.get(`${baseURL}/pledges/search`, {
        params: { searchType: 'ticketNo', searchValue: ticket },
        headers: { Authorization: `Bearer ${token}` },
      });
      const pledge = response.data.data?.pledges?.[0];
      if (!pledge || pledge.status === 'RELEASED' || pledge.loanDetails?.released) {
        setPledgeData(null);
        setError('Active pledge not found');
        return;
      }
      setPledgeData(pledge);
      setTicketNumber(pledge.loanDetails?.ticketNo || ticket);
      setSearchResults([]);
      setEntryMode('months');
      setMonths('');
      setPrincipalAmount('');
      return pledge;
    } catch (err) {
      setPledgeData(null);
      setError(err.response?.data?.message || 'Error fetching pledge');
      return null;
    }
  };

  useEffect(() => {
    const t = initialTicket || ticketFromUrl;
    if (t) fetchPledgeData(t);
  }, [initialTicket, ticketFromUrl]);

  const handleFingerprintSearch = async () => {
    try {
      setError('');
      setSearchLoading(true);
      const result = await fingerprintService.searchPledgesByScan(60);
      const matches = result?.matches || [];
      const customerCode = matches[0]?.customer?.code;
      if (!customerCode) {
        setSearchResults([]);
        setError('No pledges matched this fingerprint.');
        return;
      }
      const token = localStorage.getItem('token');
      const resp = await axios.get(`${baseURL}/pledges/client/${customerCode}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const active = (resp?.data?.pledges || []).filter((p) => p.status !== 'RELEASED' && !p.loanDetails?.released);
      setSearchResults(active);
      if (active.length === 0) setError('Fingerprint matched but no active pledges found for this customer.');
    } catch (err) {
      setError(err.message || 'Error searching pledges by fingerprint');
    } finally {
      setSearchLoading(false);
    }
  };

  const saveReceipt = async (receiptAction) => {
    const interest = round2(interestAmount);
    const principal = round2(principalAmount);
    if (interest <= 0 && principal <= 0) {
      setError('Enter interest, principal, or both.');
      return;
    }
    try {
      setLoading(true);
      setError('');
      const token = localStorage.getItem('token');
      const response = await fetch(`${baseURL}/receipts`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          date: receiptDate.toISOString(),
          transactionType: 'CPT',
          ticketNo: pledgeData.loanDetails.ticketNo,
          interestType: 'AMOUNT',
          months: entryMode === 'months' ? parseInt(months, 10) || 0 : undefined,
          interestAmount: interest,
          principalReceived: principal,
          illegalInterestAmount: interest,
          illegalPrincipalAmount: principal,
          receiptAction,
          paymentModes: [{ type: 'cash', amount: round2(interest + principal) }],
        }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to create receipt');
      setNotice(`Receipt ${data.receiptNo || ''} saved. Interest ₹${interest.toFixed(2)}, principal ₹${principal.toFixed(2)}.`);
      setInterestAmount('');
      setPrincipalAmount('');
      await fetchPledgeData(pledgeData.loanDetails.ticketNo);
    } catch (err) {
      setError(err.message || 'Failed to create receipt');
    } finally {
      setLoading(false);
    }
  };

  const sectionTitleSx = {
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: '#1e3a5f',
    mb: 0.75,
    pb: 0.5,
    borderBottom: '1px solid rgba(201,162,77,0.35)',
  };
  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      height: 32,
      borderRadius: '8px',
      fontSize: 13,
      fontWeight: 600,
      bgcolor: '#fff',
    },
  };
  const primaryBtnSx = {
    textTransform: 'none',
    fontWeight: 800,
    borderRadius: '8px',
    background: 'linear-gradient(180deg, #1e3a5f, #0b1220)',
    border: '1px solid rgba(201,162,77,0.45)',
    boxShadow: 'none',
    '&:hover': { background: 'linear-gradient(180deg, #2a4d78, #122038)', boxShadow: 'none' },
  };

  const cust = pledgeData?.customer || {};
  const custName = [
    cust.firstName,
    cust.relation && cust.relationName ? `${String(cust.relation).toUpperCase()} ${cust.relationName}` : '',
    cust.lastName,
  ].filter(Boolean).join(' ') || '—';
  const days = pledgeData && receiptDate ? calendarDaysBetween(new Date(pledgeData.date), receiptDate) : 0;

  const paidRows = paidReceiptRows(pledgeData);
  const enteredInterest = round2(interestAmount);
  const enteredPrincipal = round2(principalAmount);
  const amountRows = [
    { label: 'Principal amount', value: enteredPrincipal, bold: false },
    { label: 'Pending principal', value: Math.max(0, round2(pendingPrincipal - enteredPrincipal)), bold: false },
    { label: 'Total interest', value: enteredInterest, bold: true },
  ];

  return (
    <>
      <Box sx={embedded ? { maxHeight: '78vh', overflow: 'auto' } : { height: '100dvh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <GirviPageShell title="Create Receipt" subtitle="Pledge · Receipt" chipLabel="Pledge" maxWidth="xl" compact fitViewport={!embedded} bare={embedded} disableNavbar={embedded}>
          <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pr: 0.25, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {!embedded && <><Box sx={{ border: '1px solid #e2e8f0', borderRadius: '10px', bgcolor: '#fff', p: 1 }}>
              <Typography sx={sectionTitleSx}>Find pledge</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '160px 1fr auto 150px auto' }, gap: 0.75, alignItems: 'center' }}>
                <FormControl size="small" fullWidth sx={fieldSx}>
                  <InputLabel>Search by</InputLabel>
                  <Select
                    value={searchType}
                    label="Search by"
                    onChange={(e) => {
                      setSearchType(e.target.value);
                      setSearchValue('');
                      setSearchResults([]);
                    }}
                  >
                    <MenuItem value="firstName">Customer name</MenuItem>
                    <MenuItem value="lastName">Last name</MenuItem>
                    <MenuItem value="customerCode">Customer code</MenuItem>
                    <MenuItem value="village">Village</MenuItem>
                    <MenuItem value="mobile">Mobile</MenuItem>
                    <MenuItem value="ticketNo">Ticket number</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  size="small"
                  fullWidth
                  value={searchValue}
                  placeholder="Type to search"
                  sx={fieldSx}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSearchValue(value);
                    if (searchTimeout.current) clearTimeout(searchTimeout.current);
                    searchTimeout.current = setTimeout(() => handleSearch(value), 250);
                  }}
                  InputProps={{ endAdornment: searchLoading ? <CircularProgress size={16} /> : null }}
                />
                <Button variant="outlined" onClick={handleFingerprintSearch} disabled={searchLoading} sx={{ height: 32, textTransform: 'none', fontWeight: 700, borderRadius: '8px', whiteSpace: 'nowrap', borderColor: '#cbd5e1', color: '#1e3a5f' }}>
                  Scan finger
                </Button>
                <TextField
                  size="small"
                  label="Ticket"
                  value={ticketNumber}
                  onChange={(e) => setTicketNumber(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') fetchPledgeData(ticketNumber);
                  }}
                  sx={fieldSx}
                />
                <Button variant="contained" onClick={() => fetchPledgeData(ticketNumber)} sx={{ ...primaryBtnSx, height: 32, px: 1.5 }}>
                  Load
                </Button>
              </Box>
            </Box>

            {searchResults.length > 0 && (
              <Box sx={{ border: '1px solid #e2e8f0', borderRadius: '10px', bgcolor: '#fff', overflow: 'auto', maxHeight: 168 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {['Date', 'Ticket', 'Name', 'Mobile', 'Village', 'Principal', ''].map((h) => (
                        <TableCell key={h || 'act'} sx={{ py: 0.5, fontSize: 10.5, fontWeight: 800, letterSpacing: '0.04em', textTransform: 'uppercase', color: '#64748b', bgcolor: '#f8fafc' }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {searchResults.map((pledge) => (
                      <TableRow key={pledge._id} hover sx={{ cursor: 'pointer' }} onClick={() => fetchPledgeData(pledge.loanDetails?.ticketNo)}>
                        <TableCell sx={{ py: 0.4, fontSize: 12 }}>{formatDate(pledge.date)}</TableCell>
                        <TableCell sx={{ py: 0.4, fontSize: 12, fontWeight: 800, color: '#1e3a5f' }}>{pledge.loanDetails?.ticketNo}</TableCell>
                        <TableCell sx={{ py: 0.4, fontSize: 12 }}>{`${pledge.customer?.firstName || ''} ${pledge.customer?.lastName || ''}`}</TableCell>
                        <TableCell sx={{ py: 0.4, fontSize: 12 }}>{pledge.customer?.mobileNo}</TableCell>
                        <TableCell sx={{ py: 0.4, fontSize: 12 }}>{pledge.customer?.village}</TableCell>
                        <TableCell sx={{ py: 0.4, fontSize: 12 }} align="right">₹{Number(pledge.loanDetails?.principalAmount || 0).toLocaleString()}</TableCell>
                        <TableCell sx={{ py: 0.4 }}>
                          <Button size="small" variant="contained" sx={{ ...primaryBtnSx, height: 26, fontSize: 12 }} onClick={(e) => { e.stopPropagation(); fetchPledgeData(pledge.loanDetails?.ticketNo); }}>
                            Select
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
            </>}

            {error && <Alert severity="error" sx={{ py: 0, borderRadius: '8px' }}>{error}</Alert>}
            {notice && <Alert severity="success" sx={{ py: 0, borderRadius: '8px' }}>{notice}</Alert>}

            {pledgeData && (
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'minmax(260px, 0.85fr) 1.3fr' }, gap: 1, alignItems: 'start' }}>
                <Box sx={{ border: '1px solid #e2e8f0', borderRadius: '10px', background: 'linear-gradient(180deg, #f8fafc, #fff)', p: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ ...sectionTitleSx, flex: 1, mb: 0 }}>Pledge</Typography>
                    {days > 0 && <Chip size="small" label={`${days} days`} sx={{ height: 22, fontWeight: 800, bgcolor: '#fdebd0', color: '#d35400' }} />}
                  </Box>
                  <Typography sx={{ mt: 0.75, fontSize: 15, fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }}>{custName}</Typography>
                  <Typography sx={{ fontSize: 12, color: '#64748b', mt: 0.25 }}>
                    {[cust.mobileNo, cust.village].filter(Boolean).join(' · ') || '—'}
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.5, mt: 1 }}>
                    {[
                      ['Ticket', pledgeData.loanDetails?.ticketNo || 'N/A'],
                      ['Principal', `₹${fullPrincipal.toFixed(2)}`],
                      ['Interest', `${rate}%`],
                      ['Pledge date', formatDate(pledgeData.date)],
                    ].map(([k, v]) => (
                      <Box key={k} sx={{ bgcolor: '#fff', border: '1px solid #e2e8f0', borderRadius: '8px', px: 0.75, py: 0.4 }}>
                        <Typography sx={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#94a3b8' }}>{k}</Typography>
                        <Typography sx={{ fontSize: 13, fontWeight: 750, color: '#0f172a' }}>{v}</Typography>
                      </Box>
                    ))}
                  </Box>
                  <Box sx={{ mt: 1 }}>
                    <PledgePhotosRow clientPhoto={pledgeData.clientPhoto} articlePhoto={pledgeData.articlePhoto} size={44} />
                  </Box>
                  {paidRows.length > 0 && (
                    <Box sx={{ mt: 1, border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
                      <Typography sx={{ px: 1, py: 0.4, fontSize: 11, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#166534', bgcolor: '#dcfce7' }}>
                        Already paid
                      </Typography>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            {['Date', 'Receipt', 'Interest', 'Principal', 'Total'].map((h) => (
                              <TableCell key={h} sx={{ py: 0.35, fontSize: 10.5, fontWeight: 800, color: '#64748b' }}>{h}</TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {paidRows.map((row) => (
                            <TableRow key={row.key}>
                              <TableCell sx={{ py: 0.4, fontSize: 12 }}>{formatDate(row.date)}</TableCell>
                              <TableCell sx={{ py: 0.4, fontSize: 12, fontWeight: 700 }}>{row.no}</TableCell>
                              <TableCell align="right" sx={{ py: 0.4, fontSize: 12 }}>₹{row.interest.toFixed(2)}</TableCell>
                              <TableCell align="right" sx={{ py: 0.4, fontSize: 12 }}>₹{row.principal.toFixed(2)}</TableCell>
                              <TableCell align="right" sx={{ py: 0.4, fontSize: 12, fontWeight: 800 }}>₹{row.total.toFixed(2)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  )}
                </Box>

                <Box sx={{ border: '1px solid #e2e8f0', borderRadius: '10px', bgcolor: '#fff', p: 1 }}>
                  <Typography sx={{ ...sectionTitleSx, mb: 0.75 }}>Receipt amount</Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75, mb: 1 }}>
                    <TextField
                      size="small"
                      label="Months"
                      type="number"
                      value={months}
                      onFocus={() => setEntryMode('months')}
                      onChange={(e) => {
                        setEntryMode('months');
                        setMonths(e.target.value);
                      }}
                      inputProps={{ min: 0 }}
                      sx={fieldSx}
                    />
                    <Button size="small" variant={entryMode === 'amount' ? 'contained' : 'outlined'} onClick={() => { setEntryMode('amount'); setInterestAmount(''); }} sx={{ ...primaryBtnSx, height: 32, ...(entryMode === 'amount' ? {} : { background: '#fff', color: '#1e3a5f' }) }}>Amount</Button>
                  </Box>
                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0.75, mb: 1 }}>
                    <TextField size="small" label="Interest" type="number" value={interestAmount} onChange={(e) => setInterestAmount(e.target.value)} sx={fieldSx} />
                    <TextField size="small" label="Principal" type="number" value={principalAmount} onChange={(e) => setPrincipalAmount(e.target.value)} sx={fieldSx} />
                  </Box>
                  <Table size="small">
                    <TableBody>
                      {amountRows.map((row) => (
                        <TableRow key={row.label}>
                          <TableCell sx={{ py: 0.55, fontSize: 13, fontWeight: row.bold ? 800 : 600, borderColor: '#e2e8f0', bgcolor: row.bold ? '#fdebd0' : 'transparent' }}>{row.label}</TableCell>
                          <TableCell align="right" sx={{ py: 0.55, fontSize: 13, fontWeight: row.bold ? 800 : 700, borderColor: '#e2e8f0', bgcolor: row.bold ? '#fdebd0' : 'transparent' }}>₹{row.value.toFixed(2)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <Button variant="contained" disabled={loading} onClick={() => saveReceipt('receipt')} sx={{ ...primaryBtnSx, width: '100%', height: 34, mt: 1 }}>
                    {loading ? 'Saving...' : 'Create receipt'}
                  </Button>
                  <Box sx={{ display: 'flex', gap: 0.75, mt: 0.75 }}>
                    <Button variant="outlined" disabled={loading} onClick={() => saveReceipt('advance')} sx={{ flex: 1, height: 32, textTransform: 'none', fontWeight: 800, borderRadius: '8px' }}>Take advance</Button>
                    <Button variant="outlined" disabled={loading} onClick={() => saveReceipt('principal')} sx={{ flex: 1, height: 32, textTransform: 'none', fontWeight: 800, borderRadius: '8px' }}>Adjust in principal</Button>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </GirviPageShell>
      </Box>
    </>
  );
};

export default CreateReceipt;
